import { ApolloQueryResult } from "@apollo/client";
import { AsyncSelect } from "@neonumy/ant-design";
import TooltipButton from "views/Documents/components/TooltipButton";
import { MdEdit } from "react-icons/md";
import { clientCentralApiStrawberry, clientNeoFact } from "@neonumy/api-clients";
import { Button, Col, Form, Input, Modal, Row } from "antd";
import { LIST_CLIENT_VAT_TYPES_FOR_ASYNC_SELECT } from "queries/centralApi/clients";
import { LIST_PRODUCTS_TYPES_FOR_ASYNC_SELECT } from "queries/neoFact/catalog";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaSave } from "react-icons/fa";
import { useParams } from "react-router-dom";
import { getFormSnakeCaseError } from "utils/getFormError";
import useNotification from "utils/hooks/useNotification";
import useSales from "../../hooks/useSales";
import React from "react";
import useCoreStore from "views/hooks/useCoreStore";
import { LIST_COUNTERPARTS_QUERY } from "queries/centralApi/counterparts";
import useCustomQueryVariables from "../hooks/useCustomQueryVariables";
import { createOptionCounterPartWithCycle } from "utils/createOptionCounterPartWithCycle";

interface CreateUpdateSaleAccountsPropsTypes {
  id?: ID;
  data?: GetClientInfoResponseData | undefined;
  refetch: (
    variables?: Partial<ListClientVatAccountingConfigsVars> | undefined,
  ) => Promise<ApolloQueryResult<ListClientVatAccountingConfigsResponse>>;
  onlyCanEditCounterpart?: boolean;
}

export default function CreateUpdateSaleAccounts(
  props: CreateUpdateSaleAccountsPropsTypes,
): React.ReactElement {
  const { id, refetch, onlyCanEditCounterpart } = props;

  const urlParams: RouteParams = useParams();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const companyData = useCoreStore(state => state.companyData);
  const hasNotAccountingFirm = companyData?.hasNotAccountingFirm;
  const customQueryVariables = useCustomQueryVariables();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const { createUpdateClientVatAccounting, getClientVatAccountingConfig } = useSales(
    id,
    form,
    hasNotAccountingFirm,
  );
  const { notificationSuccessfullySaved } = useNotification();

  useEffect(() => {
    if (id && isModalVisible) {
      getClientVatAccountingConfig({
        variables: {
          id: id,
        },
      });
    }
  }, [isModalVisible]);

  const onFinish = async (values: Record<string, any>) => {
    const updateId = id ? { id } : {};
    const queryName = id ? "updateClientVatAccountingConfig" : "createClientVatAccountingConfig";
    const variables = {
      input: {
        companyId: urlParams.companyId,
        clientVatType: values.clientVatType?.value,
        productType: values.productType?.value,
        counterpartCode: hasNotAccountingFirm
          ? values.counterpartCode
          : values.counterpartCode?.value,
        ...updateId,
      },
    };

    const response = await createUpdateClientVatAccounting({ variables });

    const createErrors = response?.data?.createClientVatAccountingConfig?.errors;
    const updateErrors = response?.data?.updateClientVatAccountingConfig?.errors;

    if ((response?.data?.[queryName]?.errors?.length ?? 0) > 0) {
      const fieldsData = getFormSnakeCaseError(createErrors || updateErrors, values);
      form.setFields(fieldsData);
    } else {
      refetch();
      notificationSuccessfullySaved(t("Sale account"));
      setIsModalVisible(false);
      form.resetFields();
    }
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleOk = () => {
    form.submit();
  };
  const handleCancel = () => {
    setIsModalVisible(false);
  };

  return (
    <>
      {id ? (
        <TooltipButton placement="top" handleOnClick={showModal} icon={<MdEdit />} />
      ) : (
        <Row justify="end" className="margin-top">
          <Button type="primary" onClick={showModal}>
            {t("Add")}
          </Button>
        </Row>
      )}
      <Modal
        title={id ? t("Update sale account") : t("Add sale account")}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        okText={t("Save")}
        width={768}
        okButtonProps={{
          icon: (
            <span className="anticon">
              <FaSave />
            </span>
          ),
        }}
        className="antd-modal"
      >
        <Form
          name="createUpdateSaleAccount"
          layout="vertical"
          form={form}
          onFinish={onFinish}
          autoComplete="off"
        >
          <Row gutter={[16, 16]}>
            {!onlyCanEditCounterpart && (
              <>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="clientVatType"
                    label={t("Client vat type")}
                    rules={[{ required: true, message: t("This field is required") }]}
                  >
                    <AsyncSelect
                      hasQueryFilter={true}
                      queryOptions={{
                        client: clientNeoFact,
                        dataName: "listClientVatTypes",
                        query: LIST_CLIENT_VAT_TYPES_FOR_ASYNC_SELECT,
                        variables: {
                          filter: {
                            search: "",
                            companyId: urlParams.companyId,
                          },
                          limit: 100,
                        },
                      }}
                      optionsFormat={item => ({
                        label: `${item.code} - ${item.name}`,
                        value: item.id,
                      })}
                      translations={{
                        loadingMessage: t("Loading"),
                        noOptionsMessage: t("No Options"),
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="productType"
                    label={t("Product type")}
                    rules={[{ required: true, message: t("This field is required") }]}
                  >
                    <AsyncSelect
                      queryOptions={{
                        client: clientNeoFact,
                        dataName: "listProductTypes",
                        query: LIST_PRODUCTS_TYPES_FOR_ASYNC_SELECT,
                        variables: {
                          search: "",
                          companyId: urlParams?.companyId,
                        },
                      }}
                      translations={{
                        placeholder: t("Select product type"),
                        loadingMessage: t("Loading"),
                        noOptionsMessage: t("No Options"),
                      }}
                      optionsFormat={item => ({
                        label: `${item.code} - ${item.name}`,
                        value: item.id,
                      })}
                    />
                  </Form.Item>
                </Col>
              </>
            )}
            <Col xs={24} sm={12}>
              <Form.Item
                name="counterpartCode"
                label={t("Counterpart product")}
                rules={[{ required: true, message: t("This field is required") }]}
              >
                {hasNotAccountingFirm ? (
                  <Input />
                ) : (
                  <AsyncSelect
                    optionsFormat={createOptionCounterPartWithCycle}
                    customQueryVariables={customQueryVariables}
                    hasQueryFilter={true}
                    queryOptions={{
                      client: clientCentralApiStrawberry,
                      dataName: "listCounterpartsPaginated",
                      query: LIST_COUNTERPARTS_QUERY,
                      variables: {
                        filter: {
                          companyId: urlParams.companyId,
                          isActive: true,
                          search: "",
                          code_Istartswith_In: ["7"],
                        },
                        limit: 100,
                      },
                    }}
                    translations={{
                      placeholder: t("Select default counterpart"),
                      loadingMessage: t("Loading..."),
                      noOptionsMessage: t("No Options"),
                    }}
                  />
                )}
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}
